import type { Asset } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import type { Report } from '@/shared/api/protocol-ts/model/dto_report_pb';
import type { Tooth } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';

import { isDeleted } from './isDeleted';
import { optionalRelationMatches } from './optionalRelationMatches';

const optionalStudyRelationsMatch = (
  sourceStudyIDs: string[],
  reportSourceStudyIDs: string[],
) =>
  sourceStudyIDs.length === 0 ||
  sourceStudyIDs.every((studyID) => reportSourceStudyIDs.includes(studyID));

const numerationMatches = ({
  asset,
  tooth,
}: {
  asset: Asset;
  tooth: Tooth;
}) => {
  if (asset.Kind.case !== 'GeneratedReport') {
    return false;
  }

  const content = asset.Kind.value.Content;
  const assetNumeration =
    content.case === 'CBCTGPMPRSubVolume' || content.case === 'CBCTGPToothSlice'
      ? content.value.Numeration
      : undefined;

  if (!assetNumeration) {
    return true;
  }

  return (
    assetNumeration.ISO === tooth.Numeration?.ISO &&
    assetNumeration.SupernumeraryIndex === tooth.Numeration?.SupernumeraryIndex
  );
};

export const isToothSliceAsset = ({
  asset,
  report,
  tooth,
}: {
  asset: Asset;
  report: Report;
  tooth: Tooth;
}) => {
  if (!asset.ID || isDeleted(asset) || asset.Kind.case !== 'GeneratedReport') {
    return false;
  }

  const { Belonging: belonging, Content: content } = asset.Kind.value;

  if (
    !belonging ||
    belonging.ReportID !== report.ID ||
    belonging.ToothID !== tooth.ID ||
    !optionalRelationMatches(belonging.PatientID, report.PatientID) ||
    !optionalRelationMatches(belonging.OrganizationID, report.OrganizationID) ||
    !optionalStudyRelationsMatch(
      belonging.SourceStudyIDs,
      report.SourceStudyIDs,
    ) ||
    !numerationMatches({ asset, tooth })
  ) {
    return false;
  }

  return (
    content.case === 'CBCTGPMPRSubVolume' || content.case === 'CBCTGPToothSlice'
  );
};
